import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ComparativeLiteraturePublicityService, NoveltyCheckReportTitleService, NoveltyReviewService, OriginalInputService, RetrievalElementsService, RetrievalResultReportSubtitleInfoService, TechEssentialService, TechFeaturesService } from '../retrieval-result-report-service';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { FeatureIdentify, type FeatureRecognizeResult } from '../feature-identify';
import { Emitter, Logger } from '@patsnap/tahiti-core';
import { NoveltyCheckReportTitleApi } from '@/infrastructure/api-functions/core-drafting-api/novelty-check-report-title-api';
import { TechEssentialUpdateApi } from '@/infrastructure/api-functions/core-drafting-api/tech-essential-update-api';
import { NoveltyReportSubtitleInfoApi } from '@/infrastructure/api-functions/core-drafting-api/novelty-report-subtitle-info-api';
import { ComparisonPublicSituationApi } from '@/infrastructure/api-functions/core-drafting-api/comparison-public-situation-api';
import { NoveltyReviewApi, type NoveltyReviewResponse } from '@/infrastructure/api-functions/core-drafting-api/novelty-review-api';
import _ from 'lodash';
import { NoveltyCheckTaskStatusModel } from '../../models/novelty-check-task-status-model';
import { TaskDateFormatter } from '@/services/date-format/task-date-formatter';
import { RetrievalElementsApi } from '../../apis/retrieval-elements-api';
import type { RetrievalElement, ExtendRetrievalElementResponse, GetRetrievalElementsResponse } from '../../apis/retrieval-elements-api';
import { sleep } from '@telecom/shared/utils/task';

/**
 * 轮询延迟时间（毫秒）
 */
const NOVELTY_REVIEW_POLLING_DELAY = 1000;

/**
 * 检索要素轮询延迟时间（毫秒）
 */
const RETRIEVAL_ELEMENTS_POLLING_DELAY = 1000;

/**
 * 新颖性评述任务状态常量
 */
const NOVELTY_REVIEW_STATUS = {
    RUNNING: 'RUNNING' as const,
    SUCCESS: 'SUCCESS' as const,
    FAILED: 'FAILED' as const,
};

/**
 * 检索要素任务状态常量
 */
const RETRIEVAL_ELEMENTS_STATUS = {
    RUNNING: 'RUNNING' as const,
    SUCCESS: 'SUCCESS' as const,
    FAILED: 'FAILED' as const,
};

@injectable()
export class RetrievalResultReportServiceImpl implements
    ComparativeLiteraturePublicityService,
    RetrievalResultReportSubtitleInfoService,
    TechEssentialService,
    TechFeaturesService,
    OriginalInputService,
    NoveltyCheckReportTitleService,
    NoveltyReviewService,
    RetrievalElementsService {
    // eslint-disable-next-line max-params
    constructor(
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
        @inject(NoveltyCheckReportTitleApi)
        private readonly _noveltyCheckReportTitleApi: NoveltyCheckReportTitleApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(TechEssentialUpdateApi)
        private readonly _techSummaryUpdateApi: TechEssentialUpdateApi,
        @inject(NoveltyReportSubtitleInfoApi)
        private readonly _noveltyReportSubtitleInfoApi: NoveltyReportSubtitleInfoApi,
        @inject(ComparisonPublicSituationApi)
        private readonly _comparisonPublicSituationApi: ComparisonPublicSituationApi,
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(FeatureIdentify)
        private readonly _featureIdentify: FeatureIdentify,
        @inject(NoveltyReviewApi)
        private readonly _noveltyReviewApi: NoveltyReviewApi,
        @inject(NoveltyCheckTaskStatusModel)
        private readonly _noveltyCheckTaskStatusModel: NoveltyCheckTaskStatusModel,
        @inject(TaskDateFormatter)
        private readonly _taskDateFormatter: TaskDateFormatter,
        @inject(RetrievalElementsApi)
        private readonly _retrievalElementsApi: RetrievalElementsApi,
    ) {
        this._retrievalResultReportModel.onStatesChange(this._emitElementsStateChanges.bind(this));
    }

    /**
     * 新颖性评述轮询中断控制器
     */
    private _noveltyReviewAbortController?: AbortController;

    /**
     * 检索要素轮询中断控制器
     */
    private _retrievalElementsAbortController?: AbortController;

    async pullComparativeLiteraturePublicity(): Promise<void> {
        const data = await this._getComparisonPublicSituationRepository();
        this._retrievalResultReportModel.update((state) => {
            state.techFeaturesWithComparedPats = data;
            return state;
        });
    }

    private _onComparativeLiteraturePublicityLoadingEmitter = new Emitter<boolean>();

    get onComparativeLiteraturePublicityLoading() {
        return this._onComparativeLiteraturePublicityLoadingEmitter.event;
    }

    private async _getComparisonPublicSituationRepository() {
        this._onComparativeLiteraturePublicityLoadingEmitter.fire(true);
        const res = await this._comparisonPublicSituationApi.getComparisonPublicSituation({
            task_id: this.taskId,
        }).finally(() => this._onComparativeLiteraturePublicityLoadingEmitter.fire(false));
        const reports = _.map(res, (report) => ({
            patentId: report.patent_id,
            publicScore: report.public_score,
            relatedPara: report.related_para,
            pn: report.pn,
        }));
        return reports;
    }

    pullRetrievalResultReportSubtitleInfo(): Promise<void> {
        return this._noveltyReportSubtitleInfoRepository();
    }

    private async _noveltyReportSubtitleInfoRepository(): Promise<void> {
        const res = await this._noveltyReportSubtitleInfoApi.getNoveltyReportSubtitleInfo({
            task_id: this.taskId,
        });
        this._retrievalResultReportModel.update((state) => {
            state.creator = res.creator;
            state.generatedTime = res.generated_time;
            state.searchQty = res.search_qty;
            state.comparativeQty = res.comparative_qty;
            state.nearestQty = res.nearest_qty;
            state.secondaryApproachQty = res.secondary_approach_qty;
            return state;
        });
        this._noveltyCheckTaskStatusModel.update((state) => {
            state.lastUpdateTime = this._taskDateFormatter.format(res.generated_time);
            return state;
        });
    }

    async pullTechEssential(): Promise<string> {
        // 从模型中获取技术要点，如果模型中没有，则获取空字符串
        return this._retrievalResultReportModel.modelData.techEssential || '';
    }

    async updateTechEssential(techEssential: string): Promise<void> {
        // 先客户端更新
        this._retrievalResultReportModel.update((state) => {
            state.techEssential = techEssential;
            return state;
        });
        // 再服务端更新
        await this._techSummaryUpdateApi.updateTechSummary({
            task_id: this.taskId,
            text: techEssential,
        });
    }

    async pullNoveltyReview(): Promise<void> {
        const data = await this._getNoveltyReviewRepository();
        this._retrievalResultReportModel.update((state) => {
            state.noveltyReview = data;
            return state;
        });
    }

    private _onNoveltyReviewLoadingEmitter = new Emitter<boolean>();

    get onNoveltyReviewLoading() {
        return this._onNoveltyReviewLoadingEmitter.event;
    }

    private async _getNoveltyReviewRepository(): Promise<NoveltyReviewResponse> {
        this._onNoveltyReviewLoadingEmitter.fire(true);

        // 创建新的中断控制器
        this._noveltyReviewAbortController = new AbortController();
        let isFinished = false;
        let result: NoveltyReviewResponse = [];

        try {
            while (!isFinished) {
                const response = await this._noveltyReviewApi.getNoveltyReview({
                    taskId: this.taskId,
                }, this._noveltyReviewAbortController.signal);

                if (response.status === NOVELTY_REVIEW_STATUS.SUCCESS) {
                    result = response.result || [];
                    isFinished = true;
                } else if (response.status === NOVELTY_REVIEW_STATUS.FAILED) {
                    throw new Error(response.error_message || '新颖性评述生成失败');
                } else if (response.status === NOVELTY_REVIEW_STATUS.RUNNING) {
                    // 继续轮询，等待1秒后重试
                    await sleep(NOVELTY_REVIEW_POLLING_DELAY);
                }
            }
        } finally {
            this._onNoveltyReviewLoadingEmitter.fire(false);
            this._noveltyReviewAbortController = undefined;
        }

        return result;
    }

    /**
     * 终止新颖性评述生成
     */
    terminateNoveltyReview(): void {
        if (this._noveltyReviewAbortController) {
            this._noveltyReviewAbortController.abort();
            this._noveltyReviewAbortController = undefined;
        }
    }

    /**
     * 终止检索要素获取
     */
    terminateRetrievalElements(): void {
        if (this._retrievalElementsAbortController) {
            this._retrievalElementsAbortController.abort();
            this._retrievalElementsAbortController = undefined;
        }
    }

    async pullNoveltyCheckReportTitle(): Promise<string> {
        const title = await this._getNoveltyCheckReportTitleRepository();
        this._retrievalResultReportModel.update((state) => {
            state.title = title;
            return state;
        });
        return title;
    }

    private async _getNoveltyCheckReportTitleRepository(): Promise<string> {
        const title = await this._noveltyCheckReportTitleApi.getNoveltyCheckReportTitle({
            task_id: this.taskId,
        });
        return title;
    }

    pullOriginalInput(): Promise<string> {
        this._logger.info('RetrievalResultReportServiceImpl.pullOriginalInput unimplemented');
        return Promise.resolve('');
    }

    private get taskId() {
        return this._currentTaskService.taskId;
    }

    pullTechFeatures(): Promise<FeatureRecognizeResult> {
        return this._featureIdentify.recognize(this.taskId);
    }

    // RetrievalElementsService 接口实现
    /**
     * 检索要素加载状态
     */
    get isElementsLoading(): boolean {
        return this._retrievalResultReportModel.modelData.elementsLoading;
    }

    /**
     * 检索要素列表
     */
    get elements(): RetrievalElement[] {
        return this._retrievalResultReportModel.modelData.elements;
    }

    /**
     * 检索要素变更事件触发器
     */
    private _onElementsChangeEmitter = new Emitter<RetrievalElement[]>();
    /**
     * 检索要素变更事件
     */
    readonly onElementsChange = this._onElementsChangeEmitter.event;

    /**
     * 加载状态变更事件触发器
     */
    private _onElementsLoadingChangeEmitter = new Emitter<boolean>();
    /**
     * 加载状态变更事件
     */
    readonly onLoadingChange = this._onElementsLoadingChangeEmitter.event;

    /**
     * 发送检索要素状态变更事件
     */
    private _emitElementsStateChanges(): void {
        this._onElementsChangeEmitter.fire(this.elements);
        this._onElementsLoadingChangeEmitter.fire(this.isElementsLoading);
    }

    /**
     * 获取检索要素列表
     * @returns 检索要素列表
     */
    async pullRetrievalElements(): Promise<RetrievalElement[]> {
        const response = await this._getRetrievalElementsRepository();

        // 更新模型数据
        this._retrievalResultReportModel.update(state => ({
            ...state,
            elements: response.list,
            familyMergeEnabled: response.family_merge_enabled,
            elementsLoading: false,
        }));

        return response.list;
    }

    /**
     * 异步轮询获取检索要素数据
     * @returns 检索要素响应数据
     */
    private async _getRetrievalElementsRepository(): Promise<GetRetrievalElementsResponse> {
        const taskId = this.taskId;

        // 设置加载状态
        this._retrievalResultReportModel.update(state => ({
            ...state,
            elementsLoading: true,
        }));

        // 创建新的中断控制器
        this._retrievalElementsAbortController = new AbortController();
        let isFinished = false;
        let result: GetRetrievalElementsResponse = {
            list: [],
            family_merge_enabled: false,
        };

        try {
            while (!isFinished) {
                const response = await this._retrievalElementsApi.getRetrievalElements(
                    taskId,
                    this._retrievalElementsAbortController.signal,
                );

                if (response.status === RETRIEVAL_ELEMENTS_STATUS.SUCCESS) {
                    result = response.result || { list: [], family_merge_enabled: false };
                    isFinished = true;
                } else if (response.status === RETRIEVAL_ELEMENTS_STATUS.FAILED) {
                    throw new Error(response.error_message || '检索要素获取失败');
                } else if (response.status === RETRIEVAL_ELEMENTS_STATUS.RUNNING) {
                    // 继续轮询，等待1秒后重试
                    await sleep(RETRIEVAL_ELEMENTS_POLLING_DELAY);
                }
            }
        } catch (error) {
            // 错误处理
            this._retrievalResultReportModel.update(state => ({
                ...state,
                elementsLoading: false,
            }));

            console.error('获取检索要素失败:', error);
            throw error;
        } finally {
            this._retrievalElementsAbortController = undefined;
        }

        return result;
    }

    /**
     * 更新检索要素列表
     * @param taskId 任务ID
     * @param elements 检索要素列表
     * @param familyMergeEnabled 是否启用同族合并
     * @returns 是否更新成功
     */
    async updateRetrievalElements(elements: RetrievalElement[], familyMergeEnabled?: boolean): Promise<boolean> {
        try {
            // 设置加载状态
            this._retrievalResultReportModel.update(state => ({
                ...state,
                elements,
                elementsLoading: true,
            }));

            // 请求数据
            const response = await this._retrievalElementsApi.updateRetrievalElements({
                task_id: this.taskId,
                data: elements,
                family_merge_enabled: familyMergeEnabled,
            });

            // 更新成功后，更新本地模型数据
            if (response.success) {
                this._retrievalResultReportModel.update(state => ({
                    ...state,
                    elements,
                    elementsLoading: false,
                }));
            } else {
                this._retrievalResultReportModel.update(state => ({
                    ...state,
                    elementsLoading: false,
                }));
            }

            return response.success;
        } catch (error) {
            // 错误处理
            this._retrievalResultReportModel.update(state => ({
                ...state,
                elementsLoading: false,
            }));

            console.error('更新检索要素失败:', error);
            return false;
        }
    }

    /**
     * 扩展单个检索要素
     * @param word 需要扩展的文本
     * @returns 扩展结果
     */
    async extendRetrievalElement(word: string): Promise<ExtendRetrievalElementResponse> {
        try {
            // 请求数据
            const response = await this._retrievalElementsApi.extendRetrievalElement({
                task_id: this.taskId,
                add_word: word,
            });

            return response;
        } catch (error) {
            console.error('扩展检索要素失败:', error);
            // 返回空结果
            return {
                extend_list: [],
                ipc: [],
            };
        }
    }
}
